import {
  CreateOrganizationBrandingEmailHeaderLogoUploadUrlDTOContentTypeEnum,
  OrganizationBrandingApi,
  OrganizationBrandingLogoContentType,
  UpsertOrganizationBrandingDTOEmailHeaderLogoContentTypeEnum,
} from '@reposit/api-client';
import { get } from 'lodash';
import { call, put, takeLatest, takeLeading } from 'redux-saga/effects';
import { FlashState } from '../../components/FlashMessage';
import { getErrorMessage } from '../../utils/common.utils';
import { setFlashMessage } from '../flash-messages/flash-messages.actions';
import { createOrganizationBrandingApi, runSagaWithAuth } from '../utils/api.utils';
import {
  DELETE_ORGANIZATION_BRANDING_STORE_KEY,
  deleteOrganizationBrandingFailed,
  deleteOrganizationBrandingRequested,
  deleteOrganizationBrandingSuccess,
  FETCH_ORGANIZATION_BRANDING_STORE_KEY,
  fetchOrganizationBrandingFailed,
  fetchOrganizationBrandingRequested,
  fetchOrganizationBrandingSuccess,
  previewOrganizationBrandingEmailFailed,
  previewOrganizationBrandingEmailRequested,
  previewOrganizationBrandingEmailSuccess,
  UPSERT_ORGANIZATION_BRANDING_STORE_KEY,
  uploadOrganizationBrandingLogoFailed,
  uploadOrganizationBrandingLogoRequested,
  uploadOrganizationBrandingLogoSuccess,
  upsertOrganizationBrandingFailed,
  upsertOrganizationBrandingRequested,
  upsertOrganizationBrandingSuccess,
} from './organization-branding.actions';

const HTTP_NOT_FOUND = 404;
const MAX_LOGO_FILE_SIZE_BYTES = 2 * 1024 * 1024;
const S3_UPLOAD_TIMEOUT_MS = 30000;
const ACCEPTED_LOGO_CONTENT_TYPES = [
  CreateOrganizationBrandingEmailHeaderLogoUploadUrlDTOContentTypeEnum.Png,
  CreateOrganizationBrandingEmailHeaderLogoUploadUrlDTOContentTypeEnum.Jpeg,
];

const getAcceptedLogoContentType = (
  contentType: string,
): CreateOrganizationBrandingEmailHeaderLogoUploadUrlDTOContentTypeEnum | undefined =>
  ACCEPTED_LOGO_CONTENT_TYPES.find((acceptedContentType) => acceptedContentType === contentType);

const isAcceptedLogoContentType = (
  contentType: string,
): contentType is CreateOrganizationBrandingEmailHeaderLogoUploadUrlDTOContentTypeEnum =>
  !!getAcceptedLogoContentType(contentType);

const toUpsertLogoContentType = (contentType: string): UpsertOrganizationBrandingDTOEmailHeaderLogoContentTypeEnum => {
  const acceptedContentType = getAcceptedLogoContentType(contentType);
  if (!acceptedContentType) {
    throw new Error('Only PNG and JPEG logo files are supported.');
  }

  return acceptedContentType === CreateOrganizationBrandingEmailHeaderLogoUploadUrlDTOContentTypeEnum.Png
    ? UpsertOrganizationBrandingDTOEmailHeaderLogoContentTypeEnum.Png
    : UpsertOrganizationBrandingDTOEmailHeaderLogoContentTypeEnum.Jpeg;
};

const toPreviewLogoContentType = (contentType: string): OrganizationBrandingLogoContentType => {
  const acceptedContentType = getAcceptedLogoContentType(contentType);
  if (!acceptedContentType) {
    throw new Error('Only PNG and JPEG logo files are supported.');
  }

  return acceptedContentType === CreateOrganizationBrandingEmailHeaderLogoUploadUrlDTOContentTypeEnum.Png
    ? OrganizationBrandingLogoContentType.Png
    : OrganizationBrandingLogoContentType.Jpeg;
};

const validateLogoFileSize = (file: File): void => {
  if (file.size > MAX_LOGO_FILE_SIZE_BYTES) {
    throw new Error('Logo files must be 2 MB or smaller.');
  }
};

export async function uploadFileToS3(
  uploadUrl: string,
  file: File,
  contentType: string,
  timeoutMs = S3_UPLOAD_TIMEOUT_MS,
): Promise<void> {
  const abortController = new AbortController();
  const timeoutId = setTimeout(() => abortController.abort(), timeoutMs);

  try {
    const response = await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': contentType,
      },
      signal: abortController.signal,
    });

    if (!response.ok) {
      throw new Error(`Logo upload failed with status ${response.status}`);
    }
  } catch (error) {
    if (abortController.signal.aborted) {
      throw new Error('Logo upload timed out. Please try again.');
    }

    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
}

export function* fetchOrganizationBranding({ payload }: ReturnType<typeof fetchOrganizationBrandingRequested>) {
  try {
    const organizationBrandingApi = yield createOrganizationBrandingApi();
    const response = yield call(runSagaWithAuth(() => organizationBrandingApi.getOrganizationBranding(payload.organizationId)));

    yield put(fetchOrganizationBrandingSuccess({ organizationId: payload.organizationId, profile: response.data }));
  } catch (e) {
    if (get(e, 'response.status') === HTTP_NOT_FOUND) {
      yield put(fetchOrganizationBrandingSuccess({ organizationId: payload.organizationId, profile: null }));
      return;
    }

    const error = getErrorMessage(e);
    yield put(fetchOrganizationBrandingFailed({ organizationId: payload.organizationId, error }));
    yield put(
      setFlashMessage({
        key: FETCH_ORGANIZATION_BRANDING_STORE_KEY,
        message: error,
        state: FlashState.ERROR,
      }),
    );
  }
}

function* uploadOrganizationBrandingLogoFile(
  organizationBrandingApi: OrganizationBrandingApi,
  organizationId: string,
  logoFile: File,
) {
  const browserLogoContentType = logoFile.type;

  if (!isAcceptedLogoContentType(browserLogoContentType)) {
    throw new Error('Only PNG and JPEG logo files are supported.');
  }

  validateLogoFileSize(logoFile);

  const uploadUrlResponse = yield call(
    runSagaWithAuth(() =>
      organizationBrandingApi.createOrganizationBrandingEmailHeaderLogoUploadUrl(organizationId, {
        fileName: logoFile.name,
        contentType: browserLogoContentType,
      }),
    ),
  );

  yield call(uploadFileToS3, uploadUrlResponse.data.uploadUrl, logoFile, browserLogoContentType);

  return {
    emailHeaderLogoKey: uploadUrlResponse.data.key,
    emailHeaderLogoContentType: browserLogoContentType,
    emailHeaderLogoUrl: uploadUrlResponse.data.publicUrl,
    fileName: logoFile.name,
  };
}

export function* uploadOrganizationBrandingLogo({ payload }: ReturnType<typeof uploadOrganizationBrandingLogoRequested>) {
  try {
    const organizationBrandingApi = yield createOrganizationBrandingApi();
    const logo = yield* uploadOrganizationBrandingLogoFile(organizationBrandingApi, payload.organizationId, payload.logoFile);

    yield put(uploadOrganizationBrandingLogoSuccess({ organizationId: payload.organizationId, logo }));
  } catch (e) {
    const error = getErrorMessage(e);
    yield put(uploadOrganizationBrandingLogoFailed({ organizationId: payload.organizationId, error }));
  }
}

export function* previewOrganizationBrandingEmail({ payload }: ReturnType<typeof previewOrganizationBrandingEmailRequested>) {
  try {
    const organizationBrandingApi = yield createOrganizationBrandingApi();
    const response = yield call(
      runSagaWithAuth(() =>
        organizationBrandingApi.previewOrganizationBrandingEmail(payload.organizationId, {
          friendlyOrganizationName: payload.friendlyOrganizationName,
          emailFooterText: payload.emailFooterText,
          emailHeaderLogoKey: payload.emailHeaderLogoKey,
          emailHeaderLogoContentType: toPreviewLogoContentType(payload.emailHeaderLogoContentType),
        }),
      ),
    );

    yield put(previewOrganizationBrandingEmailSuccess({ organizationId: payload.organizationId, preview: response.data }));
  } catch (e) {
    const error = getErrorMessage(e);
    yield put(previewOrganizationBrandingEmailFailed({ organizationId: payload.organizationId, error }));
  }
}

export function* upsertOrganizationBranding({ payload }: ReturnType<typeof upsertOrganizationBrandingRequested>) {
  try {
    const organizationBrandingApi = yield createOrganizationBrandingApi();
    let emailHeaderLogoKey = payload.uploadedEmailHeaderLogoKey || payload.existingEmailHeaderLogoKey;
    let emailHeaderLogoContentType = payload.uploadedEmailHeaderLogoContentType || payload.existingEmailHeaderLogoContentType;

    if (payload.logoFile) {
      const logo = yield* uploadOrganizationBrandingLogoFile(organizationBrandingApi, payload.organizationId, payload.logoFile);

      emailHeaderLogoKey = logo.emailHeaderLogoKey;
      emailHeaderLogoContentType = logo.emailHeaderLogoContentType;
    }

    if (!emailHeaderLogoKey || !emailHeaderLogoContentType) {
      throw new Error('Select a PNG or JPEG logo before saving branding.');
    }

    const upsertLogoContentType = toUpsertLogoContentType(emailHeaderLogoContentType);
    const response = yield call(
      runSagaWithAuth(() =>
        organizationBrandingApi.upsertOrganizationBranding(payload.organizationId, {
          friendlyOrganizationName: payload.friendlyOrganizationName,
          emailFooterText: payload.emailFooterText,
          emailHeaderLogoKey,
          emailHeaderLogoContentType: upsertLogoContentType,
        }),
      ),
    );

    yield put(upsertOrganizationBrandingSuccess({ organizationId: payload.organizationId, profile: response.data }));
    yield put(
      setFlashMessage({
        key: UPSERT_ORGANIZATION_BRANDING_STORE_KEY,
        message: 'Success! Organization branding saved!',
        state: FlashState.SUCCESS,
      }),
    );
  } catch (e) {
    const error = getErrorMessage(e);
    yield put(upsertOrganizationBrandingFailed({ organizationId: payload.organizationId, error }));
    yield put(
      setFlashMessage({
        key: UPSERT_ORGANIZATION_BRANDING_STORE_KEY,
        message: error,
        state: FlashState.ERROR,
      }),
    );
  }
}

export function* deleteOrganizationBranding({ payload }: ReturnType<typeof deleteOrganizationBrandingRequested>) {
  try {
    const organizationBrandingApi = yield createOrganizationBrandingApi();
    yield call(runSagaWithAuth(() => organizationBrandingApi.deleteOrganizationBranding(payload.organizationId)));

    yield put(deleteOrganizationBrandingSuccess({ organizationId: payload.organizationId }));
    yield put(
      setFlashMessage({
        key: DELETE_ORGANIZATION_BRANDING_STORE_KEY,
        message: 'Success! Organization branding removed!',
        state: FlashState.SUCCESS,
      }),
    );
  } catch (e) {
    const error = getErrorMessage(e);
    yield put(deleteOrganizationBrandingFailed({ organizationId: payload.organizationId, error }));
    yield put(
      setFlashMessage({
        key: DELETE_ORGANIZATION_BRANDING_STORE_KEY,
        message: error,
        state: FlashState.ERROR,
      }),
    );
  }
}

export function* watchOrganizationBrandingSagas() {
  yield takeLatest(fetchOrganizationBrandingRequested.type, fetchOrganizationBranding);
  yield takeLatest(uploadOrganizationBrandingLogoRequested.type, uploadOrganizationBrandingLogo);
  yield takeLatest(previewOrganizationBrandingEmailRequested.type, previewOrganizationBrandingEmail);
  yield takeLeading(upsertOrganizationBrandingRequested.type, upsertOrganizationBranding);
  yield takeLeading(deleteOrganizationBrandingRequested.type, deleteOrganizationBranding);
}
